import type { TuneFacts } from './corpus';

export interface HarmonicNeighbour {
  tune: TuneFacts;
  score: number;
  sharedPhrases: number;
  reason: string;
}

export interface TuneRelations {
  tune: TuneFacts;
  moreByComposer: TuneFacts[];
  /** How many the library holds, so a truncated list can say so. */
  moreByComposerTotal: number;
  sharedChanges: TuneFacts[];
  sharedChangesTotal: number;
  harmonicNeighbours: HarmonicNeighbour[];
}

export function normalizeTuneText(value: string | undefined): string {
  return (value ?? '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/gi, ' ')
    .trim()
    .toLowerCase();
}

export function tuneIdentity(title: string, composer = ''): string {
  return `${normalizeTuneText(title)}::${normalizeTuneText(composer)}`;
}

function progressionSignature(tune: TuneFacts): string {
  return `${tune.bars}:${tune.romans.join('|')}`;
}

/**
 * A co-written credit ("Miles Davis, Bill Evans") names two writers, and the tune
 * belongs to both catalogues. Split before normalising — normalising eats the
 * separators.
 */
function composerCredits(composer: string | undefined): string[] {
  return (composer ?? '')
    .split(/[,&/]|\band\b/i)
    .map((name) => normalizeTuneText(name))
    .filter(Boolean);
}

function sharesCredit(credits: string[], composer: string | undefined): boolean {
  const other = composerCredits(composer);
  return credits.some((credit) => other.includes(credit));
}

function ngrams(tokens: string[], size: number): Set<string> {
  const out = new Set<string>();
  for (let index = 0; index <= tokens.length - size; index += 1) {
    out.add(tokens.slice(index, index + size).join(' → '));
  }
  return out;
}

function intersectionSize(a: Set<string>, b: Set<string>): number {
  let count = 0;
  for (const value of a) if (b.has(value)) count += 1;
  return count;
}

/** The current tune's phrase vocabulary, computed once for the whole sweep. */
interface PhraseProfile {
  length: number;
  bigrams: Set<string>;
  trigrams: Set<string>;
}

function phraseProfile(tune: TuneFacts): PhraseProfile {
  return {
    length: tune.romans.length,
    bigrams: ngrams(tune.romans, 2),
    trigrams: ngrams(tune.romans, 3),
  };
}

function phraseSimilarity(
  current: PhraseProfile,
  candidate: TuneFacts,
): { score: number; sharedPhrases: number } {
  const lengthRatio =
    Math.min(current.length, candidate.romans.length) /
    Math.max(1, current.length, candidate.romans.length);
  if (lengthRatio < 0.6) return { score: 0, sharedPhrases: 0 };

  const currentBigrams = current.bigrams;
  const candidateBigrams = ngrams(candidate.romans, 2);
  const currentTrigrams = current.trigrams;
  const candidateTrigrams = ngrams(candidate.romans, 3);
  const sharedBigrams = intersectionSize(currentBigrams, candidateBigrams);
  const sharedTrigrams = intersectionSize(currentTrigrams, candidateTrigrams);
  const bigramUnion = currentBigrams.size + candidateBigrams.size - sharedBigrams;
  const trigramUnion = currentTrigrams.size + candidateTrigrams.size - sharedTrigrams;
  const denominator = bigramUnion + 2 * trigramUnion;

  return {
    score: denominator > 0 ? (sharedBigrams + 2 * sharedTrigrams) / denominator : 0,
    sharedPhrases: sharedBigrams + sharedTrigrams,
  };
}

function uniqueTunes(tunes: TuneFacts[]): TuneFacts[] {
  const seen = new Set<string>();
  return tunes.filter((tune) => {
    const key = tuneIdentity(tune.title, tune.composer);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function byTitle(a: TuneFacts, b: TuneFacts): number {
  return a.title.localeCompare(b.title) || a.composer.localeCompare(b.composer);
}

export function buildTuneRelations(
  current: TuneFacts,
  corpus: TuneFacts[],
  limit = 4,
): TuneRelations {
  // Exclude the open tune by TITLE, not by identity. The library credits the
  // same tune differently row to row — Caravan is filed once as "Duke
  // Ellington" and once as "Duke Ellington, Juan Tizol" — so an identity match
  // left the second row in, and the panel listed the open tune as its own
  // harmonic neighbour ("Caravan · 29 shared harmonic phrases") and again under
  // More by this composer. Two genuinely different tunes DO share a title
  // occasionally, and hiding one of those is a smaller failure than telling
  // somebody the tune in front of them resembles itself.
  const currentTitle = normalizeTuneText(current.title);
  const others = uniqueTunes(
    corpus.filter((tune) => normalizeTuneText(tune.title) !== currentTitle),
  );
  const credits = composerCredits(current.composer);
  const signature = progressionSignature(current);

  const byComposer = credits.length
    ? others.filter((tune) => sharesCredit(credits, tune.composer)).sort(byTitle)
    : [];
  const moreByComposer = byComposer.slice(0, limit);

  // Every exact-signature relative is excluded from the neighbour list, not just
  // the few we have room to show: a family larger than `limit` used to spill its
  // remainder into "harmonic neighbours" at score 1.0, describing one identical
  // progression two different ways in two different columns.
  const sameChanges = others.filter((tune) => progressionSignature(tune) === signature);
  const sharedIdentities = new Set(
    sameChanges.map((tune) => tuneIdentity(tune.title, tune.composer)),
  );
  const sharedChanges = [...sameChanges].sort(byTitle).slice(0, limit);

  const profile = phraseProfile(current);
  const harmonicNeighbours = others
    .filter((tune) => !sharedIdentities.has(tuneIdentity(tune.title, tune.composer)))
    .map((tune) => {
      const similarity = phraseSimilarity(profile, tune);
      const sameForm = tune.bars === current.bars;
      return {
        tune,
        ...similarity,
        reason: `${sameForm ? 'same form' : `${tune.bars}-bar form`}; ${
          similarity.sharedPhrases
        } shared harmonic phrase${similarity.sharedPhrases === 1 ? '' : 's'}`,
      };
    })
    .filter((candidate) => candidate.score >= 0.22 && candidate.sharedPhrases >= 2)
    .sort(
      (a, b) => b.score - a.score || b.sharedPhrases - a.sharedPhrases || byTitle(a.tune, b.tune),
    )
    .slice(0, limit);

  return {
    tune: current,
    moreByComposer,
    moreByComposerTotal: byComposer.length,
    sharedChanges,
    sharedChangesTotal: sameChanges.length,
    harmonicNeighbours,
  };
}
